import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Row, Col, message, Divider, Checkbox, Typography, Alert } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { ROUTES } from '../../constants/routes';
import EventTypeService from '../../services/EventTypeService';
import VolunteerRegistrationService from '../../services/VolunteerRegistrationService';
import { StyledSelect, GlobalSelectStyles } from '../shared/StyledSelect';
import StepIndicator from './components/StepIndicator';
import SignaturePad from './components/SignaturePad';
import { 
  WAIVER_CONTENT, 
  WAIVER_VERSION, 
  MEDICAL_EXPERIENCE_OPTIONS, 
  VOLUNTEER_AREA_OPTIONS 
} from './constants';
import { 
  FormContainer, 
  FormTitle, 
  FormSection, 
  LocationTitle, 
  FormRow, 
  FieldLabel,
} from './styled';

const { Option } = Select;
const { Text, Title, Paragraph } = Typography;

// Calming color palette
const colors = {
  primary: '#4A90E2',
  secondary: '#7FB3D5',
  accent: '#D4E6F1',
  text: '#2C3E50',
  background: '#F5F9FC',
  success: '#5cb85c',
  error: '#d9534f',
  warning: '#f0ad4e',
  white: '#FFFFFF',
  gray: '#ECEFF1'
};

// Large Input component
const LargeInput = styled(Input)`
  height: 48px;
  font-size: 16px;
  padding: 8px 16px;
  
  &:focus {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

// Styled button
const StyledButton = styled(Button)`
  &.ant-btn-primary {
    background-color: ${colors.primary};
    border-color: ${colors.primary};
    border-radius: 25px;
    height: 45px;
    padding: 0 24px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 16px;
    
    &:hover, &:focus {
      background-color: ${colors.secondary};
      border-color: ${colors.secondary};
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
  
  &.ant-btn-default {
    border-radius: 25px;
    height: 45px;
    padding: 0 24px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 16px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: ${props => props.$single ? 'center' : 'space-between'};
  gap: 16px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    
    button {
      width: 100%;
    }
  }
`;

const WaiverBox = styled.div`
  background: ${colors.white};
  border: 2px solid ${colors.accent};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const WaiverHeader = styled.div`
  background: ${colors.primary};
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
`;

const EventInfoBox = styled.div`
  background: ${colors.background};
  border: 1px solid ${colors.accent};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  
  p {
    margin: 4px 0;
    font-size: 14px;
    
    strong {
      color: ${colors.text};
    }
  }
`;

const WaiverSection = styled.div`
  margin-bottom: 24px;
`;

const AcknowledgmentItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 12px;
  margin-bottom: 8px;
  background: ${props => props.$checked ? 'rgba(92, 184, 92, 0.1)' : colors.background};
  border: 1px solid ${props => props.$checked ? colors.success : colors.accent};
  border-radius: 8px;
  transition: all 0.2s ease;
  
  .ant-checkbox-wrapper {
    align-items: flex-start;
    
    .ant-checkbox {
      margin-top: 3px;
    }
  }
  
  &:hover {
    border-color: ${colors.primary};
  }
`;

const SignatureSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid ${colors.accent};
`;

const DateDisplay = styled.div`
  background: ${colors.background};
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid ${colors.accent};
  font-size: 16px;
  color: ${colors.text};
`;

const SkipButton = styled(Button)`
  &.ant-btn {
    border-radius: 25px;
    height: 45px;
    padding: 0 24px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    color: ${colors.primary};
    border-color: ${colors.primary};
    
    &:hover {
      color: ${colors.secondary};
      border-color: ${colors.secondary};
    }
  }
`;

const CityCard = styled.div`
  padding: 16px;
  border: 2px solid ${props => props.$selected ? colors.primary : colors.accent};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$selected ? 'rgba(74, 144, 226, 0.1)' : colors.white};
  
  &:hover {
    border-color: ${colors.primary};
    box-shadow: 0 2px 8px rgba(74, 144, 226, 0.15);
  }
  
  h4 {
    margin: 0;
    color: ${colors.text};
    font-size: 16px;
    font-weight: 600;
  }
  
  span {
    color: #666;
    font-size: 13px;
  }
`;

const SkipSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px dashed ${colors.accent};
  text-align: center;
  
  p {
    color: #666;
    margin-bottom: 12px;
    font-size: 14px;
  }
`;

const EventCard = styled.div`
  padding: 16px;
  border: 2px solid ${props => props.$selected ? colors.primary : colors.accent};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$selected ? 'rgba(74, 144, 226, 0.1)' : colors.white};
  
  &:hover {
    border-color: ${colors.primary};
    box-shadow: 0 2px 8px rgba(74, 144, 226, 0.15);
  }
  
  h4 {
    margin: 0 0 4px 0;
    color: ${colors.text};
    font-size: 16px;
    font-weight: 600;
  }
  
  .event-date {
    color: ${colors.primary};
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 4px;
  }
  
  .event-location {
    color: #666;
    font-size: 13px;
  }
  
  .event-type {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    margin-top: 8px;
    background: ${props => props.$typeColor || colors.accent};
    color: white;
  }
`;

// Step definitions
const STEPS = [
  { key: 'personal', title: 'Personal Info' },
  { key: 'city', title: 'Select City' },
  { key: 'event', title: 'Select Event' },
  { key: 'experience', title: 'Experience' },
  { key: 'waiver', title: 'Waiver & Sign' }
];

const VolunteerForm = () => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  const [signatureType, setSignatureType] = useState('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const [waiverAcknowledged, setWaiverAcknowledged] = useState(false);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState(null);
  const [isGeneralVolunteer, setIsGeneralVolunteer] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const navigate = useNavigate();

  // Fetch cities on mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        setCitiesLoading(true);
        const response = await EventTypeService.getPublicCities();
        if (response.success && response.data?.cities) {
          setCities(response.data.cities);
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
      } finally {
        setCitiesLoading(false);
      }
    };
    fetchCities();
  }, []);

  // Handle city selection
  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setIsGeneralVolunteer(false);
    setSelectedEvent(null); // Reset event when city changes
  };

  // Fetch events when moving to event step
  const fetchEventsForCity = async (city) => {
    try {
      setEventsLoading(true);
      // Pass city to backend — it finds all EventTypes for that city
      // and returns bookings under those EventTypes
      const response = await EventTypeService.getVolunteerEvents(city.city);
      if (response.success && response.data) {
        setEvents(response.data);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  // Handle skip - sign up as general volunteer
  const handleSkipEventSelection = () => {
    setIsGeneralVolunteer(true);
    setSelectedEvent(null);
    // Skip to experience step (step 4)
    setCurrentStep(4);
  };

  // Handle skip event selection (has city but no specific event)
  const handleSkipEvent = () => {
    setSelectedEvent(null);
    setCurrentStep(4);
  };

  // Validate current step
  const validateStep = async () => {
    try {
      if (currentStep === 1) {
        await form.validateFields(['fullName', 'phoneNumber', 'email']);
      } else if (currentStep === 2) {
        // City selection - either city selected or skipping
        if (!selectedCity && !isGeneralVolunteer) {
          message.warning('Please select a city or choose to volunteer anywhere');
          return false;
        }
      } else if (currentStep === 3) {
        // Event selection - optional, can proceed without selecting
        return true;
      } else if (currentStep === 4) {
        await form.validateFields(['medicalExperience', 'volunteerArea']);
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  // Navigation handlers
  const handleNext = async () => {
    const isValid = await validateStep();
    if (isValid) {
      // When moving from city (step 2) to events (step 3), fetch events
      if (currentStep === 2 && selectedCity) {
        fetchEventsForCity(selectedCity);
      }
      // Pre-fill printed name when entering waiver step
      if (currentStep === 4) {
        const fullName = form.getFieldValue('fullName');
        form.setFieldsValue({ printedName: fullName });
      }
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    // If general volunteer and going back from experience step, go to city step
    if (isGeneralVolunteer && currentStep === 4) {
      setIsGeneralVolunteer(false);
      setCurrentStep(2);
    } else {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Check if waiver is acknowledged
  const allAcknowledged = waiverAcknowledged;

  // Check if signature is valid
  const hasValidSignature = signatureType === 'draw' 
    ? signatureData && signatureData.length > 0
    : typedSignature && typedSignature.trim().length > 0;

  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Validate printed name
      await form.validateFields(['printedName']);
      const printedName = form.getFieldValue('printedName');

      if (!allAcknowledged) {
        message.error('Please acknowledge the waiver statement');
        return;
      }

      if (!hasValidSignature) {
        message.error('Please provide your signature');
        return;
      }

      if (!printedName || !printedName.trim()) {
        message.error('Please enter your printed name');
        return;
      }

      setLoading(true);

      // Prepare submission data - use true to get all field values including unmounted ones
      const formValues = form.getFieldsValue(true);
      
      const submissionData = {
        // Personal Info
        fullName: formValues.fullName,
        phoneNumber: formValues.phoneNumber,
        email: formValues.email,
        
        // City preference
        isGeneralVolunteer: !selectedCity,
        preferredCity: selectedCity?.city || null,
        preferredState: selectedCity?.state || null,
        
        // Selected event
        selectedEventId: selectedEvent?._id || null,
        selectedEventName: selectedEvent?.title || null,
        selectedEventDate: selectedEvent?.eventDate || null,
        
        // Experience
        medicalExperience: formValues.medicalExperience,
        volunteerArea: formValues.volunteerArea,
        
        // Single waiver acknowledgment
        acknowledgedWaiver: waiverAcknowledged,
        signatureData: signatureType === 'draw' ? signatureData : typedSignature,
        signatureType,
        typedSignature: signatureType === 'type' ? typedSignature : null,
        printedName: formValues.printedName,
        waiverVersion: WAIVER_VERSION
      };

      // Submit to backend
      const response = await VolunteerRegistrationService.submitRegistration(submissionData);

      if (response.success) {
        // Navigate to success page with registration data
        navigate(ROUTES.VOLUNTEER_SUCCESS, { 
          state: { 
            registrationId: response.data.registrationId,
            downloadToken: response.data.downloadToken,
            fullName: response.data.fullName,
            preferredCity: selectedCity ? `${selectedCity.city}, ${selectedCity.state}` : null
          } 
        });
      } else {
        message.error(response.message || 'Failed to submit registration. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error('There was an error submitting your form. Please try again.');
    } finally {
      setLoading(false);
    }
  };  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <FormSection>
            <LocationTitle>Personal Information</LocationTitle>
            <FormRow>
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="fullName"
                    label={<FieldLabel>First and Last Name</FieldLabel>}
                    rules={[{ required: true, message: 'Please enter your full name' }]}
                  >
                    <LargeInput placeholder="Enter your full name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="phoneNumber"
                    label={<FieldLabel>Phone Number</FieldLabel>}
                    rules={[{ required: true, message: 'Please enter your phone number' }]}
                  >
                    <LargeInput placeholder="Enter your phone number" />
                  </Form.Item>
                </Col>
              </Row>
            </FormRow>
            <FormRow>
              <Form.Item
                name="email"
                label={<FieldLabel>Email</FieldLabel>}
                rules={[
                  { required: true, message: 'Please enter your email address' },
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <LargeInput placeholder="Enter your email address" />
              </Form.Item>
            </FormRow>
            <ButtonRow $single>
              <StyledButton type="primary" onClick={handleNext}>
                Next Step <FontAwesomeIcon icon={faArrowRight} />
              </StyledButton>
            </ButtonRow>
          </FormSection>
        );

      case 2:
        return (
          <FormSection>
            <LocationTitle>Select Your City</LocationTitle>
            <Paragraph style={{ color: '#666', marginBottom: 24 }}>
              Choose a city where you&apos;d like to volunteer, or skip to join our general volunteer pool.
            </Paragraph>
            
            {citiesLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Text type="secondary">Loading cities...</Text>
              </div>
            ) : cities.length > 0 ? (
              <Row gutter={[16, 16]}>
                {cities.map((city, index) => (
                  <Col xs={24} sm={12} md={8} key={index}>
                    <CityCard
                      $selected={selectedCity?.city === city.city && selectedCity?.state === city.state}
                      onClick={() => handleCitySelect(city)}
                    >
                      <h4>{city.city}</h4>
                      <span>{city.state}</span>
                    </CityCard>
                  </Col>
                ))}
              </Row>
            ) : (
              <Alert
                type="info"
                message="No cities available yet"
                description="You can still sign up as a general volunteer below."
                style={{ marginBottom: 16 }}
              />
            )}
            
            <SkipSection>
              <p>Don&apos;t see your city or want to volunteer anywhere?</p>
              <SkipButton onClick={handleSkipEventSelection}>
                Skip - I&apos;ll Volunteer Anywhere
              </SkipButton>
            </SkipSection>
            
            <ButtonRow>
              <StyledButton onClick={handleBack}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </StyledButton>
              <StyledButton 
                type="primary" 
                onClick={handleNext}
                disabled={!selectedCity}
              >
                Next Step <FontAwesomeIcon icon={faArrowRight} />
              </StyledButton>
            </ButtonRow>
          </FormSection>
        );

      case 3:
        return (
          <FormSection>
            <LocationTitle>Upcoming Events in {selectedCity?.city}</LocationTitle>
            <Paragraph style={{ color: '#666', marginBottom: 24 }}>
              Select an event you&apos;d like to volunteer at, or skip to continue without choosing a specific event.
            </Paragraph>
            
            {eventsLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Text type="secondary">Loading events...</Text>
              </div>
            ) : events.length > 0 ? (
              <Row gutter={[16, 16]}>
                {events.map((event) => {
                  const eventDate = new Date(event.eventDate || event.startDate);
                  const formattedDate = eventDate.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  });
                  const isMultiDay = event.isMultiDay && event.bookingEndDate;
                  const endDate = isMultiDay ? new Date(event.bookingEndDate).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  }) : null;

                  return (
                    <Col xs={24} sm={12} key={event._id}>
                      <EventCard
                        $selected={selectedEvent?._id === event._id}
                        $typeColor={event.eventType?.color}
                        onClick={() => setSelectedEvent(event)}
                      >
                        <div className="event-date">
                          {formattedDate}{isMultiDay ? ` — ${endDate}` : ''}
                        </div>
                        <h4>{event.title}</h4>
                        <div className="event-location">{event.location?.name || 'Location TBD'}</div>
                        <span className="event-type" style={{ background: event.eventType?.color || colors.primary }}>
                          {event.eventType?.name}
                        </span>
                      </EventCard>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              <Alert
                type="info"
                message="No upcoming events found"
                description={`There are no upcoming events in ${selectedCity?.city} right now. You can still continue your registration.`}
                style={{ marginBottom: 16 }}
              />
            )}
            
            <SkipSection>
              <p>Don&apos;t want to pick a specific event?</p>
              <SkipButton onClick={handleSkipEvent}>
                Skip — Continue Without Selecting
              </SkipButton>
            </SkipSection>
            
            <ButtonRow>
              <StyledButton onClick={handleBack}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </StyledButton>
              <StyledButton 
                type="primary" 
                onClick={handleNext}
                disabled={!selectedEvent}
              >
                Next Step <FontAwesomeIcon icon={faArrowRight} />
              </StyledButton>
            </ButtonRow>
          </FormSection>
        );

      case 4:
        return (
          <FormSection>
            <LocationTitle>Volunteer Experience</LocationTitle>
            {isGeneralVolunteer && (
              <Alert
                type="info"
                message="Registering as General Volunteer"
                description={selectedCity ? `Preferred location: ${selectedCity.city}, ${selectedCity.state}` : "You'll be contacted when events are available in your area."}
                style={{ marginBottom: 16 }}
              />
            )}
            <FormRow>
              <Form.Item
                name="medicalExperience"
                label={<FieldLabel>Medical training/experience?</FieldLabel>}
                rules={[{ required: true, message: 'This field is required' }]}
              >
                <StyledSelect placeholder="Please Select">
                  {MEDICAL_EXPERIENCE_OPTIONS.map(option => (
                    <Option key={option} value={option}>{option}</Option>
                  ))}
                </StyledSelect>
              </Form.Item>
            </FormRow>
            <FormRow>
              <Form.Item
                name="volunteerArea"
                label={<FieldLabel>Choose Desired Volunteer Area</FieldLabel>}
                rules={[{ required: true, message: 'Please select your desired volunteer area' }]}
              >
                <StyledSelect placeholder="Please Select">
                  {VOLUNTEER_AREA_OPTIONS.map(option => (
                    <Option key={option.value} value={option.value}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{option.label}</div>
                        <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'normal', lineHeight: '1.3' }}>
                          {option.description}
                        </div>
                      </div>
                    </Option>
                  ))}
                </StyledSelect>
              </Form.Item>
            </FormRow>
            <ButtonRow>
              <StyledButton onClick={handleBack}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </StyledButton>
              <StyledButton type="primary" onClick={handleNext}>
                Next Step <FontAwesomeIcon icon={faArrowRight} />
              </StyledButton>
            </ButtonRow>
          </FormSection>
        );

      case 5:
        return (
          <FormSection>
            <WaiverBox>
              <WaiverHeader>
                <Title level={4} style={{ color: 'white', margin: 0 }}>
                  {WAIVER_CONTENT.title}
                </Title>
              </WaiverHeader>

              {isGeneralVolunteer && (
                <EventInfoBox>
                  <p><strong>Registration Type:</strong> General Volunteer Pool</p>
                  {selectedCity && (
                    <p><strong>Preferred Location:</strong> {selectedCity.city}, {selectedCity.state}</p>
                  )}
                </EventInfoBox>
              )}

              {selectedEvent && (
                <EventInfoBox>
                  <p><strong>Selected Event:</strong> {selectedEvent.title}</p>
                  <p><strong>Location:</strong> {selectedEvent.location?.name || 'TBD'}</p>
                  <p><strong>Date:</strong> {new Date(selectedEvent.eventDate || selectedEvent.startDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}</p>
                </EventInfoBox>
              )}

              <WaiverSection>
                <Title level={5}>Waiver Statement</Title>
                <Paragraph>{WAIVER_CONTENT.waiverStatement.intro}</Paragraph>

                <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
                  {WAIVER_CONTENT.waiverStatement.points.map((point, index) => (
                    <li key={index} style={{ marginBottom: 8, color: '#2C3E50' }}>
                      <Text>{point}</Text>
                    </li>
                  ))}
                </ul>

                <AcknowledgmentItem $checked={waiverAcknowledged}>
                  <Checkbox
                    checked={waiverAcknowledged}
                    onChange={(e) => setWaiverAcknowledged(e.target.checked)}
                  >
                    <Text strong>{WAIVER_CONTENT.waiverStatement.acknowledgmentText}</Text>
                  </Checkbox>
                </AcknowledgmentItem>
              </WaiverSection>

              <Divider />

              <WaiverSection>
                <Title level={5}>{WAIVER_CONTENT.consentRelease.title}</Title>
                <Paragraph>{WAIVER_CONTENT.consentRelease.text}</Paragraph>
              </WaiverSection>

              {!allAcknowledged && (
                <Alert
                  type="warning"
                  message="Please acknowledge the waiver statement above to proceed"
                  style={{ marginBottom: 16 }}
                />
              )}

              <SignatureSection>
                <Title level={5}>Signature</Title>
                
                <SignaturePad
                  value={signatureData}
                  onChange={setSignatureData}
                  signatureType={signatureType}
                  onSignatureTypeChange={setSignatureType}
                  typedSignature={typedSignature}
                  onTypedSignatureChange={setTypedSignature}
                  hasError={!hasValidSignature && allAcknowledged}
                />

                <Row gutter={24} style={{ marginTop: 16 }}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="printedName"
                      label={<FieldLabel>Printed Name</FieldLabel>}
                      rules={[{ required: true, message: 'Please enter your printed name' }]}
                    >
                      <LargeInput placeholder="Enter your printed name" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label={<FieldLabel>Date</FieldLabel>}>
                      <DateDisplay>
                        {new Date().toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </DateDisplay>
                    </Form.Item>
                  </Col>
                </Row>
              </SignatureSection>
            </WaiverBox>

            <ButtonRow>
              <StyledButton onClick={handleBack}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </StyledButton>
              <StyledButton 
                type="primary" 
                onClick={handleSubmit}
                loading={loading}
                disabled={!allAcknowledged || !hasValidSignature}
              >
                Submit Registration <FontAwesomeIcon icon={faArrowRight} />
              </StyledButton>
            </ButtonRow>
          </FormSection>
        );

      default:
        return null;
    }
  };

  return (
    <FormContainer>
      <GlobalSelectStyles />
      <FormTitle>Volunteer Registration</FormTitle>

      <StepIndicator steps={STEPS} currentStep={currentStep} />

      <Form
        form={form}
        layout="vertical"
        validateTrigger="onSubmit"
        requiredMark={false}
        preserve={true}
        autoComplete="off"
      >
        {renderStepContent()}
      </Form>
    </FormContainer>
  );
};

export default VolunteerForm;